import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type Ref,
} from 'vue'

type Point = { x: number; y: number }

const clamp = (value: number, bound: number) =>
  Math.max(-bound, Math.min(bound, value))

export function usePreviewZoom(
  viewport: Ref<HTMLElement | undefined>,
  scroller: Ref<HTMLElement | undefined>,
  onChange: () => void
) {
  const scale = ref(1)
  const offset = ref<Point>({ x: 0, y: 0 })
  let pinch: { distance: number; scale: number; anchor: Point } | undefined
  let pan: Point | undefined
  let tap: { point: Point; time: number } | undefined
  let lastTap: { point: Point; time: number } | undefined
  let element: HTMLElement | undefined

  const point = (touch: { clientX: number; clientY: number }): Point => {
    const rect = viewport.value!.getBoundingClientRect()
    return {
      x: touch.clientX - rect.left - rect.width / 2,
      y: touch.clientY - rect.top - rect.height / 2,
    }
  }

  const bounds = () => ({
    x: ((viewport.value?.clientWidth ?? 0) * (scale.value - 1)) / 2,
    y: ((viewport.value?.clientHeight ?? 0) * (scale.value - 1)) / 2,
  })

  const update = (x: number, y: number) => {
    const bound = bounds()
    offset.value = { x: clamp(x, bound.x), y: clamp(y, bound.y) }
    void nextTick(onChange)
  }

  const zoomTo = (value: number, focal: Point) => {
    const ratio = Math.max(1, Math.min(5, value)) / scale.value
    scale.value *= ratio
    update(
      focal.x - (focal.x - offset.value.x) * ratio,
      focal.y - (focal.y - offset.value.y) * ratio
    )
  }

  const panBy = (dx: number, dy: number) => {
    const y = offset.value.y + dy
    const overflow = y - clamp(y, bounds().y)
    if (scroller.value && overflow) {
      scroller.value.scrollTop -= overflow / scale.value
    }
    update(offset.value.x + dx, y)
  }

  const startPinch = (touches: TouchList) => {
    const a = point(touches[0])
    const b = point(touches[1])
    pinch = {
      distance: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
      scale: scale.value,
      anchor: {
        x: ((a.x + b.x) / 2 - offset.value.x) / scale.value,
        y: ((a.y + b.y) / 2 - offset.value.y) / scale.value,
      },
    }
    tap = undefined
    lastTap = undefined
  }

  const touchStart = (event: TouchEvent) => {
    if (event.touches.length >= 2) {
      event.preventDefault()
      startPinch(event.touches)
    } else if (event.touches.length === 1) {
      pan = point(event.touches[0])
      tap = { point: pan, time: event.timeStamp }
    }
  }

  const touchMove = (event: TouchEvent) => {
    if (event.touches.length >= 2) {
      event.preventDefault()
      if (!pinch) startPinch(event.touches)
      const start = pinch!
      const a = point(event.touches[0])
      const b = point(event.touches[1])
      scale.value = Math.max(
        1,
        Math.min(
          5,
          (start.scale * Math.hypot(b.x - a.x, b.y - a.y)) / start.distance
        )
      )
      update(
        (a.x + b.x) / 2 - scale.value * start.anchor.x,
        (a.y + b.y) / 2 - scale.value * start.anchor.y
      )
    } else if (event.touches.length === 1) {
      const current = point(event.touches[0])
      if (
        tap &&
        Math.hypot(current.x - tap.point.x, current.y - tap.point.y) > 10
      ) {
        tap = undefined
        lastTap = undefined
      }
      if (scale.value > 1 && pan) {
        event.preventDefault()
        panBy(current.x - pan.x, current.y - pan.y)
      }
      pan = current
    }
  }

  const touchEnd = (event: TouchEvent) => {
    if (event.touches.length >= 2) {
      startPinch(event.touches)
      return
    }
    pinch = undefined
    pan = event.touches.length ? point(event.touches[0]) : undefined
    if (pan || !tap) return
    if (event.timeStamp - tap.time < 300) {
      if (
        lastTap &&
        event.timeStamp - lastTap.time < 300 &&
        Math.hypot(
          tap.point.x - lastTap.point.x,
          tap.point.y - lastTap.point.y
        ) < 30
      ) {
        event.preventDefault()
        zoomTo(scale.value > 1 ? 1 : 2.5, tap.point)
        lastTap = undefined
      } else {
        lastTap = { point: tap.point, time: event.timeStamp }
      }
    }
    tap = undefined
  }

  const cancel = () => {
    pinch = undefined
    pan = undefined
    tap = undefined
    lastTap = undefined
  }

  const wheel = (event: WheelEvent) => {
    if (event.ctrlKey) {
      event.preventDefault()
      zoomTo(scale.value * Math.exp(-event.deltaY * 0.01), point(event))
    } else if (scale.value > 1) {
      event.preventDefault()
      const unit =
        event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? element!.clientHeight
            : 1
      panBy(-event.deltaX * unit, -event.deltaY * unit)
    }
  }

  const preventGesture = (event: Event) => event.preventDefault()

  const reset = () => {
    cancel()
    scale.value = 1
    update(0, 0)
  }

  onMounted(() => {
    element = viewport.value
    element?.addEventListener('touchstart', touchStart, { passive: false })
    element?.addEventListener('touchmove', touchMove, { passive: false })
    element?.addEventListener('touchend', touchEnd, { passive: false })
    element?.addEventListener('touchcancel', cancel)
    element?.addEventListener('wheel', wheel, { passive: false })
    element?.addEventListener('gesturestart', preventGesture, {
      passive: false,
    })
    element?.addEventListener('gesturechange', preventGesture, {
      passive: false,
    })
  })

  onBeforeUnmount(() => {
    element?.removeEventListener('touchstart', touchStart)
    element?.removeEventListener('touchmove', touchMove)
    element?.removeEventListener('touchend', touchEnd)
    element?.removeEventListener('touchcancel', cancel)
    element?.removeEventListener('wheel', wheel)
    element?.removeEventListener('gesturestart', preventGesture)
    element?.removeEventListener('gesturechange', preventGesture)
  })

  return {
    reset,
    scale: computed(() => scale.value),
    setScale: (value: number) => zoomTo(value, { x: 0, y: 0 }),
    panBy,
    viewportStyle: computed(() => ({
      touchAction: scale.value > 1 ? 'none' : 'pan-y',
    })),
    contentStyle: computed(() => ({
      transform: `translate3d(${offset.value.x}px, ${offset.value.y}px, 0) scale(${scale.value})`,
    })),
  }
}
